import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Search, X, Users, Shield, Clock, AlertTriangle, ArrowLeft, UserPlus, Send, Mail, ListOrdered } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { motion, AnimatePresence } from 'framer-motion'
import { Button, Card, CardContent } from '@/components/ui'
import { useAdmin } from '@/context/AdminContext'
import { useToast } from '@/context/ToastContext'

export function Admin() {
  const {
    families,
    isLoadingFamilies,
    searchQuery,
    selectedUser,
    isLoadingUser,
    isSaving,
    error,
    setSearchQuery,
    searchFamilies,
    selectUser,
    clearSelectedUser,
    saveAccess,
    clearOverride,
  } = useAdmin()
  const { showToast } = useToast()

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteName, setInviteName] = useState('')
  const [isSendingInvite, setIsSendingInvite] = useState(false)

  // Form state for the selected user
  const [premiumEnabled, setPremiumEnabled] = useState(false)
  const [betaEnabled, setBetaEnabled] = useState(false)
  const [disabled, setDisabled] = useState(false)
  const [expiresAt, setExpiresAt] = useState('')
  const [note, setNote] = useState('')

  // Load families on mount
  useEffect(() => {
    searchFamilies()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Sync form state when selected user changes
  useEffect(() => {
    // Defer state updates to avoid sync setState warning
    const timer = setTimeout(() => {
      if (selectedUser?.override) {
        setPremiumEnabled(selectedUser.override.premium_enabled)
        setBetaEnabled(selectedUser.override.beta_enabled)
        setDisabled(selectedUser.override.disabled)
        setExpiresAt(selectedUser.override.override_expires_at?.split('T')[0] || '')
        setNote(selectedUser.override.note || '')
      } else {
        setPremiumEnabled(false)
        setBetaEnabled(false)
        setDisabled(false)
        setExpiresAt('')
        setNote('')
      }
    }, 0)
    return () => clearTimeout(timer)
  }, [selectedUser])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    searchFamilies()
  }

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!inviteEmail.trim()) {
      showToast('Email is required', 'error')
      return
    }

    setIsSendingInvite(true)

    try {
      const session = await supabase.auth.getSession()
      const accessToken = session.data.session?.access_token

      if (!accessToken) {
        showToast('Session expired. Please refresh.', 'error')
        return
      }

      const res = await fetch('/api/invite-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          email: inviteEmail.trim(),
          name: inviteName.trim() || undefined,
        }),
      })

      const data = await res.json()

      if (data.ok) {
        showToast(`Invite sent to ${inviteEmail}`, 'success')
        setInviteEmail('')
        setInviteName('')
      } else {
        showToast(data.error || 'Failed to send invite', 'error')
      }
    } catch (_err) {
      showToast('Failed to send invite', 'error')
    } finally {
      setIsSendingInvite(false)
    }
  }

  const handleSave = async () => {
    const success = await saveAccess({
      premiumEnabled,
      betaEnabled,
      disabled,
      overrideExpiresAt: expiresAt ? new Date(expiresAt) : null,
      note: note || null,
    })

    if (success) {
      showToast('Access settings saved', 'success')
    } else {
      showToast(error || 'Failed to save', 'error')
    }
  }

  const handleClearOverride = async () => {
    const success = await clearOverride()

    if (success) {
      showToast('Override cleared', 'success')
    } else {
      showToast(error || 'Failed to clear override', 'error')
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  return (
    <div className="min-h-screen bg-[var(--color-kids-bg)]">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-[var(--color-kids-surface)] border-b border-[var(--color-kids-border)] px-4 py-3">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/dashboard"
              className="p-2 -ml-2 hover:bg-[var(--color-kids-bg)] rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-[var(--color-kids-text-muted)]" />
            </Link>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[var(--color-kids-primary)]" />
              <h1 className="text-lg font-bold text-[var(--color-kids-text)]">Admin Panel</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/admin/waitlist"
              className="flex items-center gap-2 px-4 py-2 bg-[var(--color-kids-primary)]/10 hover:bg-[var(--color-kids-primary)]/20 text-[var(--color-kids-primary)] rounded-xl transition-colors font-medium text-sm"
            >
              <ListOrdered className="w-4 h-4" />
              Waitlist
            </Link>
            <Link
              to="/admin/beta-requests"
              className="flex items-center gap-2 px-4 py-2 bg-[var(--color-kids-primary)]/10 hover:bg-[var(--color-kids-primary)]/20 text-[var(--color-kids-primary)] rounded-xl transition-colors font-medium text-sm"
            >
              <UserPlus className="w-4 h-4" />
              Beta Requests
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Invite User */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <Mail className="w-5 h-5 text-[var(--color-kids-primary)]" />
              <h2 className="font-semibold text-[var(--color-kids-text)]">Invite User</h2>
            </div>
            <form onSubmit={handleSendInvite} className="flex gap-3 flex-wrap">
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="Email address"
                required
                className="flex-1 min-w-[200px] px-4 py-2 bg-[var(--color-kids-bg)] border border-[var(--color-kids-border)] rounded-xl text-[var(--color-kids-text)] placeholder:text-[var(--color-kids-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-kids-primary)]"
              />
              <input
                type="text"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                placeholder="Name (optional)"
                className="w-40 px-4 py-2 bg-[var(--color-kids-bg)] border border-[var(--color-kids-border)] rounded-xl text-[var(--color-kids-text)] placeholder:text-[var(--color-kids-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-kids-primary)]"
              />
              <Button type="submit" disabled={isSendingInvite}>
                <Send className="w-4 h-4 mr-2" />
                {isSendingInvite ? 'Sending...' : 'Send Invite'}
              </Button>
            </form>
            <p className="text-xs text-[var(--color-kids-text-muted)] mt-3">
              Sends a magic link email. User clicks to create account.
            </p>
          </CardContent>
        </Card>

        {/* Search */}
        <form onSubmit={handleSearch} className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--color-kids-text-muted)]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by email or name..."
              className="w-full pl-10 pr-4 py-3 bg-[var(--color-kids-surface)] border border-[var(--color-kids-border)] rounded-xl text-[var(--color-kids-text)] placeholder:text-[var(--color-kids-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-kids-primary)] focus:border-transparent"
            />
            <Button
              type="submit"
              variant="primary"
              className="absolute right-2 top-1/2 -translate-y-1/2"
              disabled={isLoadingFamilies}
            >
              {isLoadingFamilies ? 'Searching...' : 'Search'}
            </Button>
          </div>
        </form>

        {/* Error display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
            {error}
          </div>
        )}

        {/* User list */}
        <Card>
          <CardContent className="p-0">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--color-kids-border)]">
              <Users className="w-4 h-4 text-[var(--color-kids-text-muted)]" />
              <span className="text-sm font-medium text-[var(--color-kids-text-muted)]">
                {families.length} user{families.length !== 1 ? 's' : ''}
              </span>
            </div>

            {isLoadingFamilies ? (
              <div className="p-8 text-center text-[var(--color-kids-text-muted)]">
                Loading users...
              </div>
            ) : families.length === 0 ? (
              <div className="p-8 text-center text-[var(--color-kids-text-muted)]">
                No users found
              </div>
            ) : (
              <div className="divide-y divide-[var(--color-kids-border)]">
                {families.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => selectUser(user.authId)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-[var(--color-kids-bg)] transition-colors text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-[var(--color-kids-text)] truncate">
                          {user.email}
                        </span>
                        {user.hasOverride && (
                          <span className="px-1.5 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                            Override
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-[var(--color-kids-text-muted)]">
                        <span>{user.name}</span>
                        <span>·</span>
                        <span className={user.tier === 'premium' ? 'text-[var(--color-kids-primary)]' : ''}>
                          {user.tier}
                        </span>
                        {user.status && (
                          <>
                            <span>·</span>
                            <span>{user.status}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-sm text-[var(--color-kids-text-muted)]">
                      {formatDate(user.createdAt)}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Slide-out panel for selected user */}
      <AnimatePresence>
        {selectedUser && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black z-40"
              onClick={clearSelectedUser}
            />

            {/* Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-[var(--color-kids-surface)] shadow-2xl z-50 overflow-y-auto"
            >
              {isLoadingUser ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="w-8 h-8 border-4 border-[var(--color-kids-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-[var(--color-kids-text-muted)]">Loading user...</p>
                  </div>
                </div>
              ) : (
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="text-xl font-bold text-[var(--color-kids-text)]">
                        {selectedUser.family?.parent_name || 'Unknown'}
                      </h2>
                      <p className="text-[var(--color-kids-text-muted)]">
                        {selectedUser.family?.parent_email}
                      </p>
                    </div>
                    <button
                      onClick={clearSelectedUser}
                      className="p-2 hover:bg-[var(--color-kids-bg)] rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5 text-[var(--color-kids-text-muted)]" />
                    </button>
                  </div>

                  {/* Current subscription info */}
                  <div className="mb-6 p-4 bg-[var(--color-kids-bg)] rounded-xl">
                    <h3 className="text-sm font-semibold text-[var(--color-kids-text-muted)] uppercase tracking-wider mb-3">
                      Subscription
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-[var(--color-kids-text-muted)]">Tier</span>
                        <span className="font-medium text-[var(--color-kids-text)]">
                          {selectedUser.family?.subscription_tier || 'free'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[var(--color-kids-text-muted)]">Status</span>
                        <span className="font-medium text-[var(--color-kids-text)]">
                          {selectedUser.family?.subscription_status || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[var(--color-kids-text-muted)]">Joined</span>
                        <span className="font-medium text-[var(--color-kids-text)]">
                          {selectedUser.family?.created_at ? formatDate(selectedUser.family.created_at) : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Effective entitlements */}
                  <div className="mb-6 p-4 bg-[var(--color-kids-bg)] rounded-xl">
                    <h3 className="text-sm font-semibold text-[var(--color-kids-text-muted)] uppercase tracking-wider mb-3">
                      Effective Access
                    </h3>
                    <div className="space-y-2 text-sm">
                      {selectedUser.effective.blocked ? (
                        <div className="flex items-center gap-2 text-red-600">
                          <AlertTriangle className="w-4 h-4" />
                          <span className="font-medium">Access Blocked</span>
                        </div>
                      ) : (
                        <>
                          <div className="flex justify-between">
                            <span className="text-[var(--color-kids-text-muted)]">Studio Mode</span>
                            <span className="font-medium text-[var(--color-kids-text)]">
                              {selectedUser.effective.studioMode}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-[var(--color-kids-text-muted)]">Premium Access</span>
                            <span className={selectedUser.effective.hasPremiumAccess ? 'text-green-600' : 'text-[var(--color-kids-text)]'}>
                              {selectedUser.effective.hasPremiumAccess ? 'Yes' : 'No'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-[var(--color-kids-text-muted)]">Beta Access</span>
                            <span className={selectedUser.effective.hasBetaAccess ? 'text-green-600' : 'text-[var(--color-kids-text)]'}>
                              {selectedUser.effective.hasBetaAccess ? 'Yes' : 'No'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-[var(--color-kids-text-muted)]">Source</span>
                            <span className="font-medium text-[var(--color-kids-text)]">
                              {selectedUser.effective.source}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Override controls */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-[var(--color-kids-text-muted)] uppercase tracking-wider mb-4">
                      Override Settings
                    </h3>

                    <div className="space-y-4">
                      {/* Premium toggle */}
                      <label className="flex items-center justify-between p-4 bg-[var(--color-kids-bg)] rounded-xl cursor-pointer hover:bg-[var(--color-kids-border)] transition-colors">
                        <div>
                          <span className="font-medium text-[var(--color-kids-text)]">Premium Enabled</span>
                          <p className="text-sm text-[var(--color-kids-text-muted)]">
                            Grant cloud studio access
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={premiumEnabled}
                          onChange={(e) => setPremiumEnabled(e.target.checked)}
                          className="w-5 h-5 rounded border-[var(--color-kids-border)] text-[var(--color-kids-primary)] focus:ring-[var(--color-kids-primary)]"
                        />
                      </label>

                      {/* Beta toggle */}
                      <label className="flex items-center justify-between p-4 bg-[var(--color-kids-bg)] rounded-xl cursor-pointer hover:bg-[var(--color-kids-border)] transition-colors">
                        <div>
                          <span className="font-medium text-[var(--color-kids-text)]">Beta Enabled</span>
                          <p className="text-sm text-[var(--color-kids-text-muted)]">
                            Access to beta features
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={betaEnabled}
                          onChange={(e) => setBetaEnabled(e.target.checked)}
                          className="w-5 h-5 rounded border-[var(--color-kids-border)] text-[var(--color-kids-primary)] focus:ring-[var(--color-kids-primary)]"
                        />
                      </label>

                      {/* Disabled toggle */}
                      <label className="flex items-center justify-between p-4 bg-red-50 rounded-xl cursor-pointer hover:bg-red-100 transition-colors">
                        <div>
                          <span className="font-medium text-red-700">Disabled</span>
                          <p className="text-sm text-red-600">
                            Block all access (shows paused screen)
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={disabled}
                          onChange={(e) => setDisabled(e.target.checked)}
                          className="w-5 h-5 rounded border-red-300 text-red-600 focus:ring-red-500"
                        />
                      </label>

                      {/* Expiration date */}
                      <div className="p-4 bg-[var(--color-kids-bg)] rounded-xl">
                        <label className="block">
                          <div className="flex items-center gap-2 mb-2">
                            <Clock className="w-4 h-4 text-[var(--color-kids-text-muted)]" />
                            <span className="font-medium text-[var(--color-kids-text)]">Expires At</span>
                          </div>
                          <input
                            type="date"
                            value={expiresAt}
                            onChange={(e) => setExpiresAt(e.target.value)}
                            className="w-full px-3 py-2 bg-[var(--color-kids-surface)] border border-[var(--color-kids-border)] rounded-lg text-[var(--color-kids-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-kids-primary)]"
                          />
                          <p className="text-xs text-[var(--color-kids-text-muted)] mt-1">
                            Leave empty for no expiration
                          </p>
                        </label>
                      </div>

                      {/* Notes */}
                      <div className="p-4 bg-[var(--color-kids-bg)] rounded-xl">
                        <label className="block">
                          <span className="font-medium text-[var(--color-kids-text)]">Notes</span>
                          <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Optional notes about this override..."
                            rows={3}
                            className="w-full mt-2 px-3 py-2 bg-[var(--color-kids-surface)] border border-[var(--color-kids-border)] rounded-lg text-[var(--color-kids-text)] placeholder:text-[var(--color-kids-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-kids-primary)] resize-none"
                          />
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="space-y-3">
                    <Button
                      variant="primary"
                      className="w-full"
                      onClick={handleSave}
                      disabled={isSaving}
                    >
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>

                    {selectedUser.override && (
                      <Button
                        variant="secondary"
                        className="w-full"
                        onClick={handleClearOverride}
                        disabled={isSaving}
                      >
                        Clear Override
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
